import { action, observable, runInAction } from "mobx";

import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { AppointmentStartingPoints } from "@libs/analytics/app-insights/app-insights.enums.ts";
import { EntityEventData } from "@libs/api/hub/EntityEventData.ts";
import { sharePendingPromise } from "@libs/decorators/sharePendingPromise.ts";
import { ClaimEpisodeOfCareDto } from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  CalendarEventStatus,
  CalendarEventType
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { SecondColumnContent } from "@shared-types/booking/second-column-content.enum.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";

import { BookingStore } from "./BookingStore.ts";

interface PatientMatchInfo {
  calendarEventId: string;
  onCreateContact?: (contactId: string) => void;
}

interface AppointmentInformationModalSettings {
  contactId: string;
  claimId?: string;
}

export interface AppointmentDialogOptions {
  type?: CalendarEventType;
  id?: string;
  initialValues?: Partial<AppointmentFormValues>;
  secondColumnContent?: SecondColumnContent | undefined;
  showAddToGroupApptDialog?: boolean;
  onCancel?: () => void;
  onSubmitted?: (values: AppointmentFormValues) => void;
}

export class BookingUi {
  constructor(private booking: BookingStore) {}
  public newAppointmentStartingPoint: string | undefined;

  // added for global appointment dialog, to avoid problems with url base approach of opening modals
  @observable
  currentAppointment: AppointmentDialogOptions | undefined = undefined;
  @observable
  formQRCodeUrl: string | undefined = undefined;

  hideFormQRCodeUrl = () => {
    runInAction(() => {
      this.formQRCodeUrl = undefined;
    });
  };
  public showFormQRCodeUrl = (val: string) => {
    runInAction(() => {
      this.formQRCodeUrl = val;
    });
  };

  @observable
  cancelCalendarEventRecurrenceId: string | undefined;

  @observable
  cancelledAppointmentDetails:
    | {
        timeOptions:
          | { startTime: DateTime | undefined; endTime: DateTime | undefined }
          | undefined;
        providerId: string | undefined;
        orgUnitId: string | undefined;
        selectedCount: number;
      }
    | undefined;

  @observable
  cancelCalendarEventId: string | undefined;

  @observable
  showCancelCalendarEventDialog: boolean;

  @observable
  isEditSingleEvent: boolean = false;

  @observable
  hidePreviousExceptions: boolean = true;

  @observable
  hidePreviousReserves: boolean = true;

  @observable
  showAttendeeCancellationDialog: boolean = false;

  @observable
  cancelCalendarEventAttendeeId: string | undefined;

  @observable
  recentlyCreatedReminderReplyId?: string;

  @observable
  showPatientMatchingDialog: boolean = false;

  patientMatchInfo: PatientMatchInfo | undefined;

  @action
  deleteCancelledAppointmentDetails = () => {
    this.cancelledAppointmentDetails = undefined;
  };

  @action
  setSecondColumnContent = (content?: SecondColumnContent) => {
    if (this.currentAppointment) {
      this.currentAppointment.secondColumnContent = content;
    }
  };

  @action
  updateSelectedCount = (count: number) => {
    if (this.cancelledAppointmentDetails) {
      this.cancelledAppointmentDetails.selectedCount = count;
    }
  };

  @action
  setCancelledAppointmentDetails = (
    timeOptions: { startTime: DateTime; endTime: DateTime },
    providerId: string,
    orgUnitId: string
  ) => {
    this.cancelledAppointmentDetails = {
      timeOptions,
      providerId,
      orgUnitId,
      selectedCount: 0
    };
  };

  @action
  setCancelCalendarEventRecurrenceId = (value?: string) => {
    this.cancelCalendarEventRecurrenceId = value;
  };

  @action
  setShowCancelCalendarEventDialog = (
    value: boolean,
    calendarEventId?: string
  ) => {
    this.showCancelCalendarEventDialog = value;
    if (value) {
      this.cancelCalendarEventId = calendarEventId;
    }
  };

  @action
  setCancelCalendarEventId = (value?: string) => {
    this.cancelCalendarEventId = value;
    if (value === undefined) {
      this.showCancelCalendarEventDialog = false;
    }
  };

  @action
  showCalendarEventDialog = (
    options: AppointmentDialogOptions,
    startingPoint?: AppointmentStartingPoints
  ) => {
    this.currentAppointment = options;
    this.newAppointmentStartingPoint = startingPoint;
  };

  @action
  onRebookOrRescheduleButtonClick = (
    calendarEvent: CalendarEvent,
    weeks?: number,
    options?: {
      isRescheduledFromIncompleteList?: boolean;
      isRescheduledFromApptBookScreen?: boolean;
    }
  ) => {
    const getStartDate = () => {
      if (options?.isRescheduledFromIncompleteList) {
        return DateTime.now().toJSDate();
      } else if (options?.isRescheduledFromApptBookScreen) {
        return DateTime.now().plus({ weeks }).toJSDate();
      }
      return calendarEvent.startDateTime.plus({ weeks }).toJSDate();
    };

    return this.showCalendarEventDialog(
      {
        type: CalendarEventType.Appointment,
        secondColumnContent: SecondColumnContent.nextAvailable,
        initialValues: {
          startDate: getStartDate(),
          startTime: calendarEvent.startTime.toTimeInputFormat(),
          duration: calendarEvent.duration.minutes,
          appointmentTypeId: calendarEvent.appointmentTypeId,
          patientId: calendarEvent.contactId,
          episodeOfCareId: calendarEvent.reason?.episodeOfCareId ?? undefined,
          providerId: calendarEvent?.userId,
          fromRescheduledAppointmentId: options?.isRescheduledFromIncompleteList
            ? calendarEvent.id
            : undefined
        }
      },
      AppointmentStartingPoints.RescheduleAppointmentButton
    );
  };

  @action
  hideCalendarEventDialog = () => {
    this.currentAppointment = undefined;
    this.newAppointmentStartingPoint = undefined;
  };

  @action
  onTogglePreviousExceptions(hidePreviousExceptions: boolean) {
    runInAction(() => {
      this.hidePreviousExceptions = hidePreviousExceptions;
    });
  }

  @action
  onTogglePreviousReserves(hidePreviousReserves: boolean) {
    runInAction(() => {
      this.hidePreviousReserves = hidePreviousReserves;
    });
  }

  @action
  setCancelCalendarEventAttendeeId = (value?: string) => {
    this.cancelCalendarEventAttendeeId = value;
  };

  @action
  setShowAttendeeCancellationDialog = (
    value: boolean,
    calendarEventId?: string,
    patientId?: string
  ) => {
    runInAction(() => {
      this.showAttendeeCancellationDialog = value;
      if (calendarEventId) {
        this.cancelCalendarEventId = calendarEventId;
      }
      if (patientId) {
        this.setCancelCalendarEventAttendeeId(patientId);
      }
    });
  };

  @action
  setShowPatientMatchingDialog = (value: boolean) => {
    this.showPatientMatchingDialog = value;
  };

  setPatientMatchInfo = (value?: PatientMatchInfo) => {
    this.patientMatchInfo = value;
  };

  startPatientMatchWorkflow = (calendarEventId: string) => {
    this.setPatientMatchInfo({
      ...this.patientMatchInfo,
      calendarEventId
    });
    this.setShowPatientMatchingDialog(true);
  };

  @observable
  lastUpdatedAppointmentTypeETag: string | undefined;

  @observable public lastUpdatedCalendarEventData:
    | Pick<EntityEventData, "etag" | "action" | "id">
    | undefined;

  @observable appointmentInformationModalSettings:
    | AppointmentInformationModalSettings
    | undefined;

  @action setAppointmentInformationModalSettings = (
    settings: AppointmentInformationModalSettings | undefined
  ) => {
    this.appointmentInformationModalSettings = settings;
  };

  @observable reviewConditionOptions:
    | {
        calendarEvent: CalendarEvent;
        calendarEventEpisodeOfCare: EpisodeOfCare | undefined;
        encounter: Encounter;
        encounterClaimEpisodeOfCare: ClaimEpisodeOfCareDto;
        encounterEpisodeOfCare: EpisodeOfCare;
      }
    | undefined;

  private _calendarEventsLoadedForContact = new Set<string>();

  /**
   * At the moment, getting calendar events using an attendee as a filter can take a very long time if there is
   * a lot of calendar events in the database.  This method aims to avoid having to do this request for a particular
   * patient ID more than once.  When new calendar events get created, they should get added to booking.calendarEventsMap
   * automatically so the array returned from this store function should always be up to date.
   *
   * This method may be able to be removed in the future if the database query is able to be optimised.
   */
  @sharePendingPromise()
  async getPatientCalendarEventsCached(
    patientId: string,
    options: { statuses: CalendarEventStatus[] } = {
      statuses: [CalendarEventStatus.Confirmed]
    }
  ): Promise<CalendarEvent[]> {
    const filterAndSort = (calendarEvents: CalendarEvent[]) => {
      const filteredEvents = calendarEvents.filter(
        calendarEvent =>
          calendarEvent.status &&
          options.statuses.includes(calendarEvent.status)
      );

      return filteredEvents.sort((a, b) =>
        compareDatesPredicate(a.startDateTime, b.startDateTime)
      );
    };

    if (this._calendarEventsLoadedForContact.has(patientId)) {
      return filterAndSort(
        Array.from(this.booking.calendarEventsMap.values()).filter(
          calendarEvent => calendarEvent.contactId === patientId
        )
      );
    }

    const result = await this.booking.getCalendarEvents({
      attendees: [patientId]
    });

    this._calendarEventsLoadedForContact.add(patientId);

    return filterAndSort(result.results);
  }
}
